import { OpenFeature, useBooleanFlagValue, useStringFlagValue } from '@openfeature/react-sdk'

import { CachedOpenFeatureClient } from '~/utils/openFeature/CachedOpenFeatureClient'

export const BOOLEAN_FEATURE_FLAGS = {
    // Make sure these values are sane for prod, as they serve as defaults in case flagsmith is unreachable.
    // The values may be overridden by flagsmith, or in E2E tests through env var VITE_FLAG_PROVIDER
    auth_page__use_unicorn_video: false,
    booking__unscheduling_dialog: false,
    cancer_features__show: false,
    closed_location_days__write: false,
    command_palette: true,
    comments_v2__surgery_type_mentions_enabled: true,
    comments_v2__user_mentions_enabled: true,
    debug__show_user_events: false,
    surgery_metadata__follow_up_self_declaration_form: false,
    surgery_metadata__follow_up_external_document: false,
    surgery_metadata__follow_up_test_results: false,
    dips__booking_letter_status: false,
    disable_critical_toasts: false,
    help__change_log_menu: false,
    hospital_configuration_expert: false,
    hospital_configuration_page__enabled: false,
    hospital_configuration_page__surgeons_enabled: false,
    hospital_configuration_page__rooms_enabled: false,
    hospital_configuration_page__statuses_enabled: false,
    inbox: false,
    insights_page__all_referral_data_allowed: false,
    insights_page__enabled: false,
    insights_page__partial_referral_data_allowed: false,
    insights_page__patient_flow: false,
    lazy_load__error_boundary: true,
    navigation_menu__admin_page: false,
    use_location_opening_hours_endpoints: false,
    use_specialties_selection: false,
    use_specialties_selection_filter: false,
    pages__retro_page: true,
    pages__timeline_view: false,
    planning_note__calculate_suggestions: false,
    planning_note__history_sync: false,
    planning_note__raw_capture: false,
    plan_metrics__use_batch_api_data: false,
    planning_note__migration_utils: false,
    planning_periods__clear_period_magic: false,
    planning_periods__mailing_list: false,
    planning_periods__prioritized_patient_groups: false,

    privacy_mode: false,
    planning_periods_expert_configuration: false,
    react_query__dev_tools: false,
    retro_page__metrics_over_time: false,
    retro_page__use_batch_api_data: true,
    surgeon_scheduler__alerts: false,
    surgeon_scheduler__allocations: false,
    surgeon_scheduler__low_predicted_waiting_list: false,
    surgeon_scheduler__model_run_options: false,
    surgeon_scheduler__recurring_assignments: false,
    surgery_duration_model__enabled: false,
    surgery_duration_model__shadow_run__enabled: false,
    surgery_scheduler__filter_on_surgeons: false,
    surgery_scheduler__ward_occupancy: false,
    version_check__enabled: false,
    waiting_list__auto_open_drawer_when_1_result: false,
    waiting_list__shadow_v6_test: false,
    periods_overview__show_unfinished_surgeries: false,
}

export type BooleanFeatureFlagName = keyof typeof BOOLEAN_FEATURE_FLAGS

export const NUMBER_FEATURE_FLAGS = {
    planning_note__raw_capture_batch_size: 500,
}

const API_VERSION_FLAGS = {
    dips__saga_status_api_version: { default: 'v2', valid: ['v2'] as const },
    dips__schedule_surgery_api_version: { default: 'v3', valid: ['v3'] as const },
    dips__scheduled_surgeries_api_version: { default: 'v4', valid: ['v4'] as const },
    dips__un_scheduled_surgeries_api_version: { default: 'v3', valid: ['v3', 'v6'] as const },
} as const

type ApiVersionFlagName = keyof typeof API_VERSION_FLAGS
type ApiVersionFlagValue<T extends ApiVersionFlagName> = (typeof API_VERSION_FLAGS)[T]['valid'][number]

function isApiVersionFlag(flagName: StringFeatureFlagName): flagName is ApiVersionFlagName {
    return flagName in API_VERSION_FLAGS
}

const API_VERSION_DEFAULTS = Object.fromEntries(Object.entries(API_VERSION_FLAGS).map(([k, v]) => [k, v.default])) as {
    [K in ApiVersionFlagName]: (typeof API_VERSION_FLAGS)[K]['default']
}

export type PlanningNoteWriteMode = 'legacy' | 'dual' | 'migrated'

export const STRING_FEATURE_FLAGS = {
    ...API_VERSION_DEFAULTS,
    planning_note__write_mode: 'legacy' as string, // Roll out through legacy -> dual -> migrated.
} as const

export type StringFeatureFlagName = keyof typeof STRING_FEATURE_FLAGS

export type DipsScheduledSurgeriesApiVersion = ApiVersionFlagValue<'dips__scheduled_surgeries_api_version'>
export type DipsUnScheduledSurgeriesApiVersion = ApiVersionFlagValue<'dips__un_scheduled_surgeries_api_version'>
export type DipsSagaStatusApiVersion = ApiVersionFlagValue<'dips__saga_status_api_version'>
export type DipsScheduleSurgeryApiVersion = ApiVersionFlagValue<'dips__schedule_surgery_api_version'>

// NOTE: The hooks (i.e. useFlag and useStringFlag) should be preferred if possible (e.g. inside React lifecycle).

// Cache repeated getFlag()/getStringFlag() lookups in performance-sensitive code; hooks subscribe to OpenFeature directly.
const client = new CachedOpenFeatureClient(OpenFeature.getClient())

/**
 * This returns whether the flag is enabled or not. It does not return the flag value.
 */
export function useFlag(flagName: BooleanFeatureFlagName): boolean {
    const defaultValue = BOOLEAN_FEATURE_FLAGS[flagName]
    return useBooleanFlagValue(flagName, defaultValue)
}

/**
 * This returns the flag value, narrowed to the values the flag declares.
 * Mirrors the overloads on {@link getStringFlag}.
 */
export function useStringFlag<T extends ApiVersionFlagName>(flagName: T): ApiVersionFlagValue<T>
export function useStringFlag<T extends StringFeatureFlagName>(flagName: T): (typeof STRING_FEATURE_FLAGS)[T]
export function useStringFlag<T extends StringFeatureFlagName>(flagName: T): string {
    const defaultValue = STRING_FEATURE_FLAGS[flagName]
    const value = useStringFlagValue(flagName, defaultValue)

    if (isApiVersionFlag(flagName)) {
        const apiFlag = API_VERSION_FLAGS[flagName]
        return apiFlag.valid.some(validValue => validValue === value) ? value : apiFlag.default
    }

    return value
}

/**
 * This returns whether the flag is enabled or not. It does not return the flag value.
 */
export function getFlag(flagName: BooleanFeatureFlagName): boolean {
    return client.getBooleanValue(flagName, BOOLEAN_FEATURE_FLAGS[flagName])
}

export function getNumberFlag(flagName: keyof typeof NUMBER_FEATURE_FLAGS): number {
    return client.getNumberValue(flagName, NUMBER_FEATURE_FLAGS[flagName])
}

/**
 * This returns the flag value.
 */
export function getStringFlag<T extends ApiVersionFlagName>(flagName: T): ApiVersionFlagValue<T>
export function getStringFlag<T extends StringFeatureFlagName>(flagName: T): string
export function getStringFlag<T extends StringFeatureFlagName>(flagName: T): string {
    const value = client.getStringValue(flagName, STRING_FEATURE_FLAGS[flagName])

    // Log invalid API version values to Sentry, as they can be hard to spot in regular API errors
    if (isApiVersionFlag(flagName)) {
        const apiFlag = API_VERSION_FLAGS[flagName]

        if (!apiFlag.valid.some(v => v === value)) {
            console.error(`Invalid API version for flag "${flagName}": "${value}". Valid values: ${apiFlag.valid.map(v => `"${v}"`).join(', ')}`)
        }
    }

    return value
}
